import { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Modal as AntdModal } from 'antd';

import ScriptContext from './editor/ScriptContext';
import BlockPalette from './editor/BlockPalette';
import EventCanvas from './editor/EventCanvas';
import ScriptingHelp from './ScriptingHelp';

const EVENT_TABS = [
  { index: 0, label: 'On Start',  description: 'Runs when the record is opened' },
  { index: 1, label: 'On Change', description: 'Runs when this field changes' },
  { index: 2, label: 'On Save',   description: 'Runs when the record is saved' },
];

export default function BlockEditor({
  id,
  comps,
  setComps,
  setIsShowingScript,
  vars,
  setVars,
  externals,
  setExternals,
  creationIndex,
  setCreationIndex,
  initialTab,
}) {
  const [activeTab, setActiveTab] = useState(initialTab || 0);
  const [linked, setLinked] = useState(true);
  const [showImportExport, setShowImportExport] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const [zoom, setZoom] = useState(1.1);
  const [drillStack, setDrillStack] = useState([]);
  const [showHelp, setShowHelp] = useState(false);

  const pushDrill = (entry) => setDrillStack(s => [...s, entry]);
  const popDrill  = () => setDrillStack(s => s.slice(0, -1));

  const zoomIn  = () => setZoom(z => Math.min(1.5, Math.round((z + 0.1) * 10) / 10));
  const zoomOut = () => setZoom(z => Math.max(0.7, Math.round((z - 0.1) * 10) / 10));

  // Undo/redo — history lives only for this editor session.
  const [historyStack, setHistoryStack] = useState(() => [
    JSON.parse(JSON.stringify(comps[id].script)),
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < historyStack.length - 1;

  // commitComps: runs linked-sync, pushes a history entry, then calls parent setComps.
  // All script mutations go through here so undo/redo stays consistent.
  const commitComps = (newComps) => {
    if (linked) {
      const startChildren  = newComps[id]?.script?.[0]?.children;
      const changeChildren = newComps[id]?.script?.[1]?.children;
      const oldStart  = JSON.stringify(comps[id]?.script?.[0]?.children);
      const oldChange = JSON.stringify(comps[id]?.script?.[1]?.children);
      const newStart  = JSON.stringify(startChildren);
      const newChange = JSON.stringify(changeChildren);
      if (newStart !== oldStart) {
        newComps[id].script[1].children = JSON.parse(JSON.stringify(startChildren));
      } else if (newChange !== oldChange) {
        newComps[id].script[0].children = JSON.parse(JSON.stringify(changeChildren));
      }
    }

    // Only push when the script actually changed.
    if (JSON.stringify(newComps[id]?.script) !== JSON.stringify(comps[id]?.script)) {
      const entry = JSON.parse(JSON.stringify(newComps[id].script));
      // Truncate any redo entries above current position, then append.
      setHistoryStack(prev => [...prev.slice(0, historyIndex + 1), entry]);
      setHistoryIndex(prev => prev + 1);
    }

    setComps(newComps);
  };

  const undo = () => {
    if (!canUndo) return;
    const newIndex = historyIndex - 1;
    const snapshot = JSON.parse(JSON.stringify(historyStack[newIndex]));
    const newComps = JSON.parse(JSON.stringify(comps));
    newComps[id].script = snapshot;
    setHistoryIndex(newIndex);
    setComps(newComps);
  };

  const redo = () => {
    if (!canRedo) return;
    const newIndex = historyIndex + 1;
    const snapshot = JSON.parse(JSON.stringify(historyStack[newIndex]));
    const newComps = JSON.parse(JSON.stringify(comps));
    newComps[id].script = snapshot;
    setHistoryIndex(newIndex);
    setComps(newComps);
  };

  const context = {
    compId: id,
    comps,
    clipboard,
    setClipboard,
    setComps: commitComps,
    vars,
    setVars,
    externals,
    setExternals,
    creationIndex,
    setCreationIndex,
    drillStack,
    pushDrill,
    popDrill,
  };

  const clearAll = () => {
    const newComps = JSON.parse(JSON.stringify(comps));
    for (const event of newComps[id].script) {
      event.children = [];
    }
    commitComps(newComps);
  };

  const exportScript = (e) => {
    e.preventDefault();
    const text = JSON.stringify(comps[id].script);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'AtlasScript Component';
    link.href = url;
    link.click();
  };

  const importScript = (e) => {
    e.preventDefault();
    if (!uploadedFile) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (
          Array.isArray(parsed) && parsed.length === 3 &&
          parsed[0].type === 'event/start' &&
          parsed[1].type === 'event/change' &&
          parsed[2].type === 'event/save'
        ) {
          const newComps = JSON.parse(JSON.stringify(comps));
          newComps[id].script[0].children = newComps[id].script[0].children.concat(parsed[0].children);
          newComps[id].script[1].children = newComps[id].script[1].children.concat(parsed[1].children);
          newComps[id].script[2].children = newComps[id].script[2].children.concat(parsed[2].children);
          commitComps(newComps);
        }
      } catch (_) {}
    };
    reader.readAsText(uploadedFile);
  };

  const activeEvent = EVENT_TABS[activeTab];

  return (
    <DndProvider backend={HTML5Backend}>
      <ScriptContext.Provider value={context}>
        <style>{`
          @keyframes script-spin-beam {
            from { transform: scale(1.5) rotate(0deg); }
            to   { transform: scale(1.5) rotate(360deg); }
          }
          @keyframes script-glow-pulse {
            0%, 100% { box-shadow: 0 0 18px 4px rgba(99,102,241,0.22), 0 8px 40px rgba(0,0,0,0.12); }
            50%       { box-shadow: 0 0 32px 8px rgba(139,92,246,0.30), 0 8px 40px rgba(0,0,0,0.12); }
          }
        `}</style>

        {/* Glow wrapper — positioning + animated border glow */}
        <div className="script-editor-root" style={{
          position: 'fixed',
          left: 0, right: 0, top: 0, bottom: 0,
          margin: 'auto',
          marginTop: '5vh',
          width: '90vw',
          height: '90vh',
          maxWidth: '100vw',
          borderRadius: '9px',
          padding: '2px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          zIndex: 9,
          animation: 'script-glow-pulse 4s ease-in-out infinite',
        }}>

          {/* Spinning conic-gradient — the flowing beam of light */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: [
              'conic-gradient(from 0deg,',
              '  rgba(147,197,253,0)    0deg,',
              '  rgba(147,197,253,0.75) 35deg,',
              '  rgba(167,139,250,0.95) 70deg,',
              '  rgba(196,181,253,0.90) 100deg,',
              '  rgba(110,231,183,0.75) 140deg,',
              '  rgba(110,231,183,0)    190deg,',
              '  rgba(147,197,253,0)    360deg',
              ')',
            ].join(''),
            animation: 'script-spin-beam 5s linear infinite',
            zIndex: 0,
          }} />

          {/* Inner modal — solid background sits on top of the glow */}
          <div style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            height: '100%',
            backgroundColor: '#f7f8fc',
            borderRadius: '7px',
            overflow: 'hidden',
            color: '#111827',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Work Sans, sans-serif',
          }}>
          {/* ── Toolbar ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 16px',
            borderBottom: '1px solid #e5e7eb',
            gap: '8px',
            flexShrink: 0,
            backgroundColor: '#ffffff',
          }}>
            <span style={{
              color: '#111827',
              fontWeight: 400,
              fontSize: '1.1vmax',
              marginRight: '8px',
              letterSpacing: '-0.01em',
            }}>
              Edit Script
            </span>

            {/* Event tabs */}
            {EVENT_TABS.map((tab) => (
              <button
                key={tab.index}
                title={tab.description}
                onClick={() => { setActiveTab(tab.index); setDrillStack([]); }}
                style={{
                  padding: '4px 12px',
                  border: activeTab === tab.index ? 'none' : '1px solid #e5e7eb',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontFamily: 'Work Sans, sans-serif',
                  fontSize: '0.8vmax',
                  fontWeight: activeTab === tab.index ? 600 : 400,
                  backgroundColor: activeTab === tab.index ? '#111827' : '#ffffff',
                  color: activeTab === tab.index ? '#ffffff' : '#374151',
                  transition: 'all 0.15s',
                }}
              >
                {tab.label}
                {linked && tab.index < 2 && (
                  <span style={{ fontSize: '0.6vmax', opacity: 0.5, marginLeft: '4px' }}>⛓</span>
                )}
              </button>
            ))}

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Undo / Redo */}
            <button
              title="Undo"
              onClick={undo}
              disabled={!canUndo}
              style={{
                padding: '4px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: canUndo ? 'pointer' : 'default',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.75vmax',
                backgroundColor: '#ffffff',
                color: canUndo ? '#374151' : '#d1d5db',
              }}
            >
              ↺ Undo
            </button>
            <button
              title="Redo"
              onClick={redo}
              disabled={!canRedo}
              style={{
                padding: '4px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: canRedo ? 'pointer' : 'default',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.75vmax',
                backgroundColor: '#ffffff',
                color: canRedo ? '#374151' : '#d1d5db',
              }}
            >
              ↻ Redo
            </button>

            {/* Zoom controls */}
            <button
              title="Zoom out"
              onClick={zoomOut}
              disabled={zoom <= 0.7}
              style={{
                padding: '4px 9px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: zoom > 0.7 ? 'pointer' : 'default',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.75vmax',
                backgroundColor: '#ffffff',
                color: zoom > 0.7 ? '#374151' : '#d1d5db',
              }}
            >
              −
            </button>
            <span style={{
              fontSize: '0.72vmax',
              color: '#9ca3af',
              fontFamily: 'Work Sans, sans-serif',
              minWidth: '2.8em',
              textAlign: 'center',
              userSelect: 'none',
            }}>
              {Math.round((zoom - 0.1) * 100)}%
            </span>
            <button
              title="Zoom in"
              onClick={zoomIn}
              disabled={zoom >= 1.5}
              style={{
                padding: '4px 9px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: zoom < 1.5 ? 'pointer' : 'default',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.75vmax',
                backgroundColor: '#ffffff',
                color: zoom < 1.5 ? '#374151' : '#d1d5db',
              }}
            >
              +
            </button>

            {/* Divider */}
            <div style={{ width: '1px', height: '20px', backgroundColor: '#e5e7eb', margin: '0 2px' }} />

            {/* Link start↔change */}
            <button
              title={linked ? 'Start and Change are linked — click to unlink' : 'Click to link Start and Change events'}
              onClick={() => setLinked((l) => !l)}
              style={{
                padding: '4px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.75vmax',
                backgroundColor: linked ? '#111827' : '#ffffff',
                color: linked ? '#ffffff' : '#374151',
              }}
            >
              {linked ? '⛓ Linked' : '⛓ Unlinked'}
            </button>

            {/* Import/Export */}
            <button
              onClick={() => setShowImportExport((s) => !s)}
              style={{
                padding: '4px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.75vmax',
                backgroundColor: showImportExport ? '#111827' : '#ffffff',
                color: showImportExport ? '#ffffff' : '#374151',
              }}
            >
              Import / Export
            </button>

            {/* Help */}
            <button
              onClick={() => setShowHelp(true)}
              title="Scripting help"
              style={{
                padding: '4px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.75vmax',
                backgroundColor: '#ffffff',
                color: '#374151',
              }}
            >
              ? Help
            </button>

            {/* Clear */}
            <button
              onClick={clearAll}
              title="Clear all commands from all events"
              style={{
                padding: '4px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.75vmax',
                backgroundColor: '#ffffff',
                color: '#374151',
              }}
            >
              Clear All
            </button>

            {/* Done */}
            <button
              onClick={() => setIsShowingScript(false)}
              style={{
                padding: '4px 14px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'Work Sans, sans-serif',
                fontSize: '0.8vmax',
                fontWeight: 600,
                backgroundColor: '#111827',
                color: '#ffffff',
              }}
            >
              Done
            </button>
          </div>

          {/* ── Scripting help ── */}
          <AntdModal className="cf-modal" title="Scripting — Quick Guide" width={680}
            visible={showHelp} footer={null} onOk={() => setShowHelp(false)} onCancel={() => setShowHelp(false)}>
            <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
              <ScriptingHelp />
            </div>
          </AntdModal>

          {/* ── Import/Export panel ── */}
          {showImportExport && (
            <div style={{
              padding: '8px 16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: '#f9fafb',
              flexShrink: 0,
            }}>
              <a
                href="#export"
                onClick={exportScript}
                style={{
                  fontSize: '0.8vmax',
                  color: '#2563eb',
                  fontFamily: 'Work Sans, sans-serif',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Export JSON
              </a>
              <span style={{ color: '#d1d5db' }}>|</span>
              <input
                type="file"
                onChange={(e) => setUploadedFile(e.target.files[0])}
                style={{
                  fontSize: '0.75vmax',
                  color: '#374151',
                  fontFamily: 'Work Sans, sans-serif',
                }}
              />
              <a
                href="#import"
                onClick={importScript}
                style={{
                  fontSize: '0.8vmax',
                  color: '#2563eb',
                  fontFamily: 'Work Sans, sans-serif',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Import JSON
              </a>
            </div>
          )}

          {/* ── Drill breadcrumb bar ── */}
          {drillStack.length > 0 && (() => {
            const { breadcrumb } = drillStack[drillStack.length - 1];
            return (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 16px',
                gap: '6px',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                flexShrink: 0,
                flexWrap: 'wrap',
              }}>
                <button
                  onClick={popDrill}
                  style={{
                    padding: '2px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: 'Work Sans, sans-serif',
                    fontSize: '0.72vmax',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    flexShrink: 0,
                  }}
                >
                  ← Back
                </button>
                <div style={{ width: '1px', height: '14px', backgroundColor: '#e5e7eb', flexShrink: 0 }} />
                {breadcrumb.map((label, i) => (
                  <span key={i} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontFamily: 'Work Sans, sans-serif',
                    fontSize: '0.72vmax',
                    color: i === breadcrumb.length - 1 ? '#111827' : '#9ca3af',
                    fontWeight: i === breadcrumb.length - 1 ? 500 : 400,
                  }}>
                    {i > 0 && <span style={{ color: '#d1d5db' }}>›</span>}
                    {label}
                  </span>
                ))}
              </div>
            );
          })()}

          {/* ── Main layout: palette + canvas ── */}
          {/* Outer clips; inner is scaled so content always fills the bounds exactly. */}
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <div style={{
              transformOrigin: 'top left',
              transform: `scale(${zoom})`,
              width: `${(1 / zoom) * 100}%`,
              height: `${(1 / zoom) * 100}%`,
              display: 'flex',
              padding: '12px 16px',
              gap: '16px',
            }}>
              <BlockPalette />
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  fontSize: '0.7vmax',
                  color: '#9ca3af',
                  fontFamily: 'Work Sans, sans-serif',
                  marginBottom: '8px',
                  letterSpacing: '0.06em',
                }}>
                  {activeEvent.description}
                </div>
                <EventCanvas eventIndex={activeTab} />
              </div>
            </div>
          </div>
          </div>{/* end inner modal */}
        </div>{/* end glow wrapper */}
      </ScriptContext.Provider>
    </DndProvider>
  );
}
